import type {
  CoachIdentityIngestionIssue,
  CoachIdentityInputKind,
  CoachIdentityReference,
} from "@/lib/behavior/behaviorContracts";
import {
  buildCoachIdentityIngestionReport,
} from "@/lib/behavior/coachIdentityRuntime";
import type {
  CoachPhilosophyProfile,
  CoachVoiceProfile,
} from "@/lib/behavior/behaviorContracts";

export const COACH_IDENTITY_TEMPLATE_VERSION = "coach_identity_template_v1";

export const COACH_IDENTITY_SUPPORTED_DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/rtf",
  "text/rtf",
  "text/plain",
  "text/markdown",
] as const;

export const COACH_IDENTITY_SUPPORTED_DOCUMENT_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".ppt",
  ".pptx",
  ".rtf",
  ".txt",
  ".md",
] as const;

export interface CoachIdentityInputRule {
  kind: CoachIdentityInputKind;
  activeSource: "authoritative" | "supporting";
  activatesRuntimeDirectly: boolean;
  description: string;
  supported: string[];
  notGuaranteed: string[];
}

export const COACH_IDENTITY_INPUT_RULES: Record<CoachIdentityInputKind, CoachIdentityInputRule> = {
  template: {
    kind: "template",
    activeSource: "authoritative",
    activatesRuntimeDirectly: true,
    description: "Template content is the primary source of truth for coach identity.",
    supported: [
      "Structured markdown sections for philosophy and voice.",
      "Inline links and attachment manifests as supporting references.",
    ],
    notGuaranteed: [
      "Malformed sections will not be activated automatically.",
      "Linked or attached content does not silently become operative instructions.",
    ],
  },
  link: {
    kind: "link",
    activeSource: "supporting",
    activatesRuntimeDirectly: false,
    description: "Links are stored as supporting references and require explicit extraction before runtime use.",
    supported: [
      "Public http/https URLs.",
      "Optional coach notes describing what matters from the linked source.",
    ],
    notGuaranteed: [
      "Arbitrary private sites are not guaranteed to be readable.",
      "A valid URL alone does not activate identity behavior.",
    ],
  },
  document: {
    kind: "document",
    activeSource: "supporting",
    activatesRuntimeDirectly: false,
    description: "Documents are supporting references until parsed and confirmed.",
    supported: [
      "PDF, DOC, DOCX, PPT, PPTX, RTF, TXT, and Markdown files.",
      "Optional extracted notes that can later become confirmed identity inputs.",
    ],
    notGuaranteed: [
      "Some office files may still require pasted text before they can be normalized.",
      "Uploaded files do not directly change runtime behavior without normalization.",
    ],
  },
};

function trimText(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function normalizeNotes(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    const normalized = trimText(value);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    result.push(normalized);
  });
  return result;
}

function hasSupportedDocumentExtension(fileName: string | null): boolean {
  const normalized = (fileName || "").toLowerCase();
  return COACH_IDENTITY_SUPPORTED_DOCUMENT_EXTENSIONS.some((extension) => normalized.endsWith(extension));
}

export function isSupportedCoachIdentityDocument(input: {
  fileName?: string | null;
  mimeType?: string | null;
}): boolean {
  const mimeType = trimText(input.mimeType);
  if (mimeType && COACH_IDENTITY_SUPPORTED_DOCUMENT_MIME_TYPES.includes(mimeType as typeof COACH_IDENTITY_SUPPORTED_DOCUMENT_MIME_TYPES[number])) {
    return true;
  }
  return hasSupportedDocumentExtension(trimText(input.fileName));
}

export function normalizeCoachIdentityLinkReference(input: {
  id?: string | null;
  title?: string | null;
  sourceUrl?: string | null;
  extractedNotes?: Array<string | null | undefined>;
}): CoachIdentityReference {
  const sourceUrl = trimText(input.sourceUrl);
  const notes = normalizeNotes(input.extractedNotes || []);
  const isSupported = Boolean(sourceUrl && /^https?:\/\//i.test(sourceUrl));

  return {
    id: input.id ?? null,
    kind: "link",
    title: trimText(input.title),
    sourceUrl,
    fileName: null,
    mimeType: null,
    status: notes.length > 0 && isSupported ? "parsed" : isSupported ? "reference_only" : "unsupported",
    selectedForRuntime: false,
    extractedNotes: notes,
    failureReason: isSupported ? null : "Only public http/https links are supported.",
  };
}

export function normalizeCoachIdentityDocumentReference(input: {
  id?: string | null;
  title?: string | null;
  fileName?: string | null;
  mimeType?: string | null;
  extractedNotes?: Array<string | null | undefined>;
}): CoachIdentityReference {
  const fileName = trimText(input.fileName);
  const mimeType = trimText(input.mimeType);
  const notes = normalizeNotes(input.extractedNotes || []);
  const isSupported = isSupportedCoachIdentityDocument({ fileName, mimeType });

  return {
    id: input.id ?? null,
    kind: "document",
    title: trimText(input.title) || fileName,
    sourceUrl: null,
    fileName,
    mimeType,
    status: notes.length > 0 && isSupported ? "parsed" : isSupported ? "reference_only" : "unsupported",
    selectedForRuntime: false,
    extractedNotes: notes,
    failureReason: isSupported ? null : "Only PDF, DOC, DOCX, PPT, PPTX, RTF, TXT, and Markdown documents are supported.",
  };
}

export function summarizeCoachIdentityIngestion(params: {
  philosophyProfile: CoachPhilosophyProfile;
  voiceProfile: CoachVoiceProfile;
  references?: CoachIdentityReference[];
}): {
  references: CoachIdentityReference[];
  issues: CoachIdentityIngestionIssue[];
  report: ReturnType<typeof buildCoachIdentityIngestionReport>;
} {
  const references = params.references || [];
  const issues: CoachIdentityIngestionIssue[] = [];

  references.forEach((reference, index) => {
    if (reference.status === "unsupported") {
      issues.push({
        level: "warning",
        field: `references[${index}]`,
        message: reference.failureReason || "Reference is not supported and will remain reference-only.",
      });
    }
    if (reference.status === "failed") {
      issues.push({
        level: "error",
        field: `references[${index}]`,
        message: reference.failureReason || "Reference processing failed.",
      });
    }
  });

  return {
    references,
    issues,
    report: buildCoachIdentityIngestionReport({
      philosophyProfile: params.philosophyProfile,
      voiceProfile: params.voiceProfile,
      references,
      issues,
    }),
  };
}
